import React, { Fragment, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Modal } from 'reactstrap';
import IntlMessages from 'util/IntlMessages';
import {
    showIssueLoader,
    onSaveIssue,
    onIssueClose,
} from 'actions/issue';
import { inputFields, displayedImage, showErrors, modalHeader, isFormValid, getFormError, checkDatesValid } from '../common';
import { isValueEntered } from 'services/Validation';
import moment from 'moment';
import { isDateBefore } from 'services/DateService';
import { ISSUE_TYPE_RELEASE } from 'constants/project';

const IssueAdd = () => {
    const dispatch = useDispatch();
    const addIssueState = useSelector(state => state.issues.addIssueState);
    const allMembers = useSelector(state => state.members.allMember);
    const allFeatures = useSelector(state => state.features.allFeature);
    const [selectedIssue, setSelectedIssue] = useState(initProps);
    const [formErrors, setFormErrors] = useState({ name: '' });
    const [formValid, setFormValid] = useState(false);

    useEffect(() => {
        setSelectedIssue(initProps());
    }, [addIssueState]);

    // using selectedIssue makes it behave like componentDidUpdate - effect takes place when selectedIssue changes
    useEffect(() => {
        setFormValid(false);
        validateForm();
    }, [selectedIssue]);

    const initProps = () => {
        return {
            id: '',
            orgid: '',
            name: '',
            description: '',
            type: '',
            startDate: '',
            endDate: '',
            releaseEnv: '',
            releaseDate: '',
            releaseCutoffDate: '',
            starred: false,
            frequently: false,
        };
    };

    const handleUserInput = (event, isNumber) => {
        const name = event.target.name;
        let value = event.target.value;
        if (isNumber) {
            value = Number(event.target.value);
        }
        updateSelectedIssueField(name, value);
        validateField(name, value);
    };

    const handleDateInput = (fieldName, value) => {
        updateSelectedIssueField(fieldName, value);
        validateDateFields(name, value);
        validateForm();
    };

    const updateSelectedIssueField = (fieldName, value) => {
        const newIssue = { ...selectedIssue };
        newIssue[fieldName] = value;
        setSelectedIssue(newIssue);
    };

    const validateDateFields = (name, value) => {
        formErrors['dates'] = '';
        formErrors['releaseDate'] = ''; // may be mandatory if type = Release
        formErrors['releaseDates'] = ''; // check
        switch (name) {
            case 'endDate':
                // check its not before the start date
                if (isValueEntered(value) && isValueEntered(selectedIssue?.startDate)) {
                    if (isDateBefore(moment(value), moment(selectedIssue?.startDate))) {
                        formErrors['dates'] = ' are invalid. The end date selected cannot be before the start date';
                    }
                }
                break;
            case 'startDate':
                // check its not after the end date
                if (isValueEntered(selectedIssue?.endDate) && isValueEntered(value)) {
                    if (isDateBefore(moment(selectedIssue?.endDate), moment(value))) {
                        formErrors['dates'] = ' are invalid. The start date selected cannot be after the end date';
                    }
                }
                break;
            case 'releaseDate':
                // check its not before the start date
                if (ISSUE_TYPE_RELEASE === selectedIssue?.type) {
                    if (isValueEntered(value) && isValueEntered(selectedIssue?.releaseCutoffDate)) {
                        if (isDateBefore(moment(value), moment(selectedIssue?.releaseCutoffDate))) {
                            formErrors['releaseDates'] = ' are invalid. The release date selected cannot be before the release cutoff date';
                        }
                    }
                }
                break;
            case 'releaseCutoffDate':
                // check its not before the start date
                if (ISSUE_TYPE_RELEASE === selectedIssue?.type) {
                    if (isValueEntered(selectedIssue?.releaseDate) && isValueEntered(value)) {
                        if (isDateBefore(moment(selectedIssue?.releaseDate), moment(value))) {
                            formErrors['releaseDates'] = ' are invalid. The release cutoff date selected cannot be after the release date';
                        }
                    }
                }
                break;
            default:
                break;
        }
    };

    const validateField = (fieldName, value) => {
        formErrors[fieldName] = getFormError(fieldName, value, selectedIssue);
        setFormErrors(formErrors);
        validateForm();
    };

    const validateForm = () => {
        setFormValid(isFormValid(selectedIssue) && checkDatesValid(selectedIssue));
    };

    const maintainButton = (label) => {
        return (
            <Button className="text-uppercase" disabled={!formValid} color="primary" onClick={() => {
                dispatch(showIssueLoader());
                dispatch(onIssueClose());
                dispatch(onSaveIssue(selectedIssue));
                setSelectedIssue(initProps());
                setFormValid(false);
            }}>
                {label}
            </Button>
        );
    };

    const modalHeaderOnClick = () => {
        return () => {
            setFormValid(false);
            dispatch(onIssueClose());
        };
    };

    const modalBody = (buttonText) => {
        return (
            <Fragment>
                <div className="modal-box-content">
                    <div className="row no-gutters">
                        {displayedImage(selectedIssue)}
                        {showErrors(formErrors)}
                        {inputFields(selectedIssue, allFeatures, allMembers, handleUserInput, handleDateInput)}
                    </div>
                </div>

                <div className="modal-box-footer d-flex flex-row">
                    {maintainButton(buttonText)}
                </div>
            </Fragment>
        );
    };
    
    return (
        <Modal className="modal-box" isOpen={addIssueState}>
            {modalHeader(<IntlMessages id="issue.addIssue" />, modalHeaderOnClick)}
            {modalBody(<IntlMessages id="issue.addIssue" />)}
        </Modal>
    );
};

export default IssueAdd;


// WEBPACK FOOTER //
// ./components/Agile/issue/issueMaintenance/issueAdd/index.js